import { Box, Grid, Link, Typography } from "@mui/material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { CTextField } from "@/components/TextField/CTextField";
import { Button, ButtonVariantOption } from "@/components/Button";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { KeyboardArrowLeft } from "@mui/icons-material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import React, { useEffect, useRef, useState } from "react";
import { useFeatureFlags } from "@/configs/featureFlag";
import { PropertySummaryFeedGrid } from "../components/PropertySummaryFeedGrid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { SizingWrapperStyle } from "@/components/Wrapper";
import EditModal from "@/features/propertySummary/components/EditModal";
import {
  type PropertySummarySearchForm,
  PropertySummarySearchSchema,
} from "@/features/propertySummary/types/schemas";

// 絞り込みを行う処理(未実装)
const onValid = async (): Promise<void> => {
  // async request which may result error
  try {
    // await fetch()
  } catch (e) {
    // handle your error
  }
  await new Promise<void>((resolve) => {
    resolve();
  });
};

export const PropertySummaryList: React.FC = () => {
  const { mypageTop, propertySummary } = useFeatureFlags();

  const { control, handleSubmit } = useForm<PropertySummarySearchForm>({
    mode: "all",
    defaultValues: {
      keyword: "",
    },
    resolver: zodResolver(PropertySummarySearchSchema),
  });

  // FeedGridに更新を指示するためのref
  const ref = useRef<{ reload: () => void }>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [sheetId, setSheetId] = useState<number | null>(null);

  const handleClose = (
    event: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    setEditModalOpen(false);
  };

  // sheetIdが設定されたときに自動的にモーダルを開く
  useEffect(() => {
    if (sheetId !== null) {
      setEditModalOpen(true);
    }
  }, [sheetId]);

  // モーダルが閉じられたときにsheetIdをリセット
  useEffect(() => {
    if (!editModalOpen && sheetId !== null) {
      // モーダルが閉じられたら、少し遅延してsheetIdをリセット（アニメーション完了を待つ）
      const timer = setTimeout(() => {
        setSheetId(null);
      }, 300);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [editModalOpen, sheetId]);

  // propertySummaryが無効の場合は/へ遷移する
  if (propertySummary === false) {
    return <Navigate to="/" replace />;
  }

  return (
    <PagePaper>
      {/* ページタイトル */}
      <Box sx={{ display: "flex", mb: 3 }}>
        <MonitorHeartIcon sx={{ mr: 1 }} fontSize="large" />
        <PageTitle>物件概要書一覧</PageTitle>
      </Box>

      {/* 絞り込み検索 */}
      <Box display={"none"}>
        <SectionPaper>
          <Grid container columnSpacing={2} rowSpacing={1} py={3} px={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">絞り込み検索</Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2} alignItems={"center"}>
                <Grid item xs={12} sm={9}>
                  <CTextField
                    label="住居表示、地番、家屋番号で検索"
                    control={control}
                    name={"keyword"}
                    size={SizingWrapperStyle.INHERIT}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button
                    label={"検索"}
                    variant={ButtonVariantOption.Contained}
                    size={SizingWrapperStyle.INHERIT}
                    onClick={() => {
                      handleSubmit(onValid);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </SectionPaper>
      </Box>

      <Box sx={{ mt: 3 }} />

      <SectionPaper>
        <Grid container columnSpacing={2} rowSpacing={1} py={3} px={2}>
          <Grid item xs={12}>
            <PropertySummaryFeedGrid
              ref={ref}
              filterCondition={{ keyword: "" }}
              setEditModalOpen={setEditModalOpen}
              setSheetId={setSheetId}
            />
          </Grid>
        </Grid>
      </SectionPaper>

      {/* 戻る */}
      <Box sx={{ pt: 2 }}>
        <Link
          component={RouterLink}
          to={mypageTop ? "/mypage" : "/feed"}
          sx={{ display: "inline-block" }}
        >
          <Box sx={{ display: "flex" }}>
            <KeyboardArrowLeft />
            <Typography>
              {mypageTop ? "マイページに戻る" : "不動産の権利異動検索に戻る"}
            </Typography>
          </Box>
        </Link>
      </Box>
      <EditModal
        sheetId={sheetId}
        key={sheetId ?? 0}
        open={editModalOpen && sheetId !== null}
        setOpen={setEditModalOpen}
        handleClose={handleClose}
      />
    </PagePaper>
  );
};
